/**
 * Mine / My group / Everyone at the firm.
 *
 * LIVE as of CC-50150 part 2. The scope goes to the server (`GET /reports/jobs
 * ?scope=`), which resolves "my group" from CB's own membership data — the client
 * never asserts who its peers are.
 *
 * It was previously rendered DISABLED rather than faked, because filtering one
 * user's own rows and labelling the result "Everyone at the firm" shows a partial
 * list as if it were complete. `enabled` is kept as an escape hatch so the control
 * can be re-gated per environment without deleting it.
 *
 * Note what widening does NOT do: a job's stored result stays readable only by the
 * user who ran it (it carries their row-level data scope), so other people's rows
 * appear as activity but aren't openable.
 */
const QueueScopeSelect = ({ value = 'mine', onChange, enabled = true }) => (
  <div className="flex items-center gap-1.5">
    {/* The same words as the tooltip below, which is the only text a sighted user gets
        for this control: "jobs" is our internal noun for them, "report runs" is the
        one the whole Queue page uses at the user. A screen-reader label and a hover
        label describing one control in two vocabularies is the drift worth spending a
        line on. */}
    <label htmlFor="queue-scope" className="sr-only">Whose report runs to list</label>
    <select
      id="queue-scope"
      value={value}
      disabled={!enabled}
      onChange={(e) => onChange?.(e.target.value)}
      title={enabled ? 'Whose report runs to list. Other people’s results stay private to them.' : 'Firm-wide visibility needs tenant-scoped listing on the server — coming soon.'}
      // 2026-08-26 ADA sweep: `.select-rc` replaces `border-gray-200 rounded-lg
      // px-2.5 py-1.5 text-[12.5px] text-gray-700`. #e5e7eb is 1.24:1 and this
      // control is white-on-white, so its 1px border is the only thing marking
      // it as a control (SC 1.4.11 → 3.0:1; --color-rc-control is 3.03:1). The
      // geometry utilities are deleted rather than paired — `.select-rc` is
      // unlayered and would have made every one of them dead code.
      //
      // The `enabled` escape hatch above still works, but its disabled dressing
      // had to change with it: `disabled:bg-gray-50` / `disabled:text-gray-400`
      // / `disabled:cursor-not-allowed` all lose to `.select-rc`'s unlayered
      // background-color, color and cursor. `opacity` is untouched by the class,
      // so `disabled:opacity-50` survives and matches what `.btn-rc:disabled`
      // and `.input-rc:disabled` already do. If this control is ever re-gated,
      // check on screen that it LOOKS inert — that was CC-50277's whole point.
      className="select-rc disabled:opacity-50"
    >
      <option value="mine">Mine</option>
      <option value="group">My group</option>
      <option value="firm">Everyone at the firm</option>
    </select>
  </div>
)

export default QueueScopeSelect
